import { fetchCurrentHoldingVT2 } from 'views/Portfolio/components/Details/services/fetchCurrentHoldings';
import { ToolExecutionResult, HoldingData } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

type SortableField = 'profitAndLoss' | 'totalValue' | 'profitAndLossPct' | 'vintage' | 'wineName';

export const executeGetCurrentHoldings = async (args: Record<string, unknown>): Promise<ToolExecutionResult> => {
  try {
    const sortBy = (args.sortBy as SortableField) || 'totalValue';
    const sortDirection = (args.sortDirection as 'asc' | 'desc') || 'desc';
    const limit = (args.limit as number) || 10;

    const response = await fetchCurrentHoldingVT2();

    if (!response.data || response.data.length === 0) {
      return {
        success: true,
        data: {
          message: 'No holdings found in portfolio',
          holdings: [],
        },
      };
    }

    // Map and transform the data
    let holdings: HoldingData[] = response.data.map((product) => ({
      lwin18: product.lwin18,
      wineName: product.wineName,
      vintage: product.vintage || null,
      region: product.cultWinesAllocationRegion || 'Unknown',
      qty: product.qty,
      totalValue: product.totalValue || 0,
      totalCost: product.totalCost || 0,
      profitAndLoss: product.profitAndLoss || 0,
      profitAndLossPct:
        product.totalCost && product.totalCost > 0 ? ((product.profitAndLoss || 0) / product.totalCost) * 100 : 0,
      valuePerUnit: product.valuePerUnit || 0,
      costPerUnit: product.costPerUnit || 0,
      highestBidPrice: product.highestBid?.priceGbp ?? product.highestBidPrice ?? null,
      lowestOfferPrice: product.lowestOffer?.priceGbp ?? product.lowestOfferPrice ?? null,
    }));

    // Sort the data
    holdings.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortBy) {
        case 'wineName':
          aVal = a.wineName;
          bVal = b.wineName;
          break;
        case 'vintage':
          aVal = a.vintage || '';
          bVal = b.vintage || '';
          break;
        case 'profitAndLoss':
          aVal = a.profitAndLoss;
          bVal = b.profitAndLoss;
          break;
        case 'profitAndLossPct':
          aVal = a.profitAndLossPct;
          bVal = b.profitAndLossPct;
          break;
        case 'totalValue':
        default:
          aVal = a.totalValue;
          bVal = b.totalValue;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    // Limit results
    holdings = holdings.slice(0, limit);

    // Calculate portfolio totals
    const totalValue = response.data.reduce((sum, h) => sum + (h.totalValue || 0), 0);
    const totalCost = response.data.reduce((sum, h) => sum + (h.totalCost || 0), 0);
    const totalPnL = response.data.reduce((sum, h) => sum + (h.profitAndLoss || 0), 0);
    const totalQty = response.data.reduce((sum, h) => sum + h.qty, 0);

    // Format for LLM consumption
    const formattedHoldings = holdings.map((h, index) => ({
      rank: index + 1,
      wine: `${h.wineName}${h.vintage ? ` ${h.vintage}` : ''}`,
      region: h.region,
      quantity: `${h.qty} unit${h.qty !== 1 ? 's' : ''}`,
      totalValue: formatCurrency(h.totalValue),
      totalCost: formatCurrency(h.totalCost),
      profitAndLoss: `${formatCurrency(h.profitAndLoss)} (${formatPercentage(h.profitAndLossPct)})`,
      valuePerUnit: formatCurrency(h.valuePerUnit),
      hasActiveBid: h.highestBidPrice !== null,
      highestBid: h.highestBidPrice ? formatCurrency(h.highestBidPrice) : null,
    }));

    return {
      success: true,
      data: {
        holdings: formattedHoldings,
        sortedBy: sortBy,
        sortDirection,
        showing: holdings.length,
        totalHoldings: response.data.length,
        portfolioSummary: {
          totalValue: formatCurrency(totalValue),
          totalCost: formatCurrency(totalCost),
          totalProfitAndLoss: formatCurrency(totalPnL),
          totalProfitAndLossPct: totalCost > 0 ? formatPercentage((totalPnL / totalCost) * 100) : '0%',
          totalUnits: totalQty,
          uniqueWines: response.data.length,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch current holdings: ${(error as Error).message}`,
    };
  }
};
